import type { CleaningOptions, OutputMode, SourceFormat } from '../types/app';
import { defaultCleaningOptions } from '../config/site';

export type PageKey = 'home' | 'formatting' | 'chatgpt-word' | 'pdf' | 'markdown' | 'linebreaks' | 'whatsapp' | 'html' | 'analysis' | 'pdf-export' | 'imprint' | 'privacy' | '404';

export interface FaqItem {
  question: string;
  answer: string;
}

export interface PageContent {
  key: PageKey;
  eyebrow: string;
  title: string;
  intro: string;
  quickAnswer: string;
  useCases: string[];
  mode: OutputMode;
  source: SourceFormat;
  options: CleaningOptions;
  before: string;
  after: string;
  guideTitle: string;
  guide: string[];
  faqs: FaqItem[];
}

const standardFaqs: FaqItem[] = [
  {
    question: 'Wird mein eingefügter Text hochgeladen?',
    answer: 'Nein. Die Bereinigung läuft ausschließlich in deinem Browser. Kopivaro sendet den Inhalt weder an einen Server noch an eine KI-API.',
  },
  {
    question: 'Muss ich ein Konto erstellen?',
    answer: 'Nein. Das Werkzeug funktioniert ohne Anmeldung. Beim Neuladen der Seite wird der eingefügte Inhalt verworfen.',
  },
  {
    question: 'Welche Formatierungen bleiben erhalten?',
    answer: 'Das hängt vom Ziel ab. Word und E-Mail behalten Überschriften, Listen, Hervorhebungen und Links. Klartext entfernt bewusst sämtliche Rich-Text-Formatierung.',
  },
  {
    question: 'Kann ich die automatische Bereinigung anpassen?',
    answer: 'Ja. Jede wichtige Bereinigung lässt sich einzeln ein- oder ausschalten. Das Original im Eingabefeld bleibt dabei unverändert.',
  },
  {
    question: 'Kann ich Dateien öffnen und das Ergebnis als PDF speichern?',
    answer: 'Ja. PDF, DOCX, HTML, Markdown und TXT werden lokal geöffnet. Das bereinigte Ergebnis kannst du unter anderem als PDF, DOCX, HTML, Markdown oder TXT herunterladen.',
  },
];

function preset(overrides: Partial<CleaningOptions> = {}): CleaningOptions {
  return { ...defaultCleaningOptions, ...overrides };
}

export const pages: Record<Exclude<PageKey, 'imprint' | 'privacy' | '404'>, PageContent> = {
  home: {
    key: 'home',
    eyebrow: 'Einfügen ohne Chaos',
    title: 'Text online bereinigen und Formatierung entfernen',
    intro: 'Entferne störende Formatierungen, Markdown, doppelte Leerzeichen und PDF-Zeilenumbrüche oder bereite Text gezielt für Word, E-Mail, WhatsApp und Websites auf.',
    quickAnswer: 'Kopivaro ist ein kostenloser Online-Textreiniger für kopierte Inhalte. Das Werkzeug entfernt oder vereinheitlicht störende Formatierungen und erzeugt wahlweise Klartext, sauberes HTML, Markdown, WhatsApp-Text oder formatierte Inhalte für Word und E-Mail – vollständig lokal im Browser.',
    useCases: ['ChatGPT- und KI-Text bereinigen', 'Formatierung aus kopiertem Text entfernen', 'PDF-Zeilenumbrüche und Silbentrennungen reparieren', 'Text für Word, E-Mail, WhatsApp oder eine Website vorbereiten'],
    mode: 'word',
    source: 'auto',
    options: preset(),
    before: '**Projektstatus**\n\nDer Entwurf wurde in der PDF\nauf mehrere Zeilen umgebro-\nchen.  Nächster Schritt : Freigabe.\n\n- Konzept prüfen\n- Rückmeldung senden',
    after: 'Projektstatus\n\nDer Entwurf wurde in der PDF auf mehrere Zeilen umgebrochen. Nächster Schritt: Freigabe.\n\n• Konzept prüfen\n• Rückmeldung senden',
    guideTitle: 'So entstehen saubere Texte',
    guide: [
      'Text einfügen oder PDF, DOCX, HTML, Markdown beziehungsweise TXT lokal öffnen.',
      'Das vermutete Quellformat prüfen und bei Bedarf korrigieren.',
      'Ein Ziel wie Word, E-Mail, WhatsApp, Klartext, HTML oder Markdown wählen.',
      'Änderungen und Textanalyse kontrollieren, dann kopieren oder als PDF, DOCX und weitere Formate herunterladen.',
    ],
    faqs: standardFaqs,
  },
  formatting: {
    key: 'formatting',
    eyebrow: 'Sauberer Text ohne Altlasten',
    title: 'Text-Formatierung online entfernen, Absätze behalten',
    intro: 'Entferne Schriftarten, Farben, HTML-Reste, Markdown-Zeichen und überflüssige Abstände aus kopiertem Text, ohne sinnvolle Absätze und Listen zu verlieren.',
    quickAnswer: 'Mit Kopivaro kannst du Text-Formatierung online entfernen, ohne den Inhalt hochzuladen. Der Modus „Sauberer Klartext“ entfernt Rich-Text- und HTML-Formatierung, normalisiert Leerzeichen und behält echte Absätze sowie lesbare Listen.',
    useCases: ['Formatierung aus Word- oder Website-Text entfernen', 'Kopierten Text in Klartext umwandeln', 'Schriftarten, Farben und HTML-Reste entfernen', 'Absätze und Listen trotz Bereinigung erhalten'],
    mode: 'plain',
    source: 'auto',
    options: preset({ repairPdfBreaks: false }),
    before: 'Projektstatus\n\nDieser Text enthält  fremde Schriftarten, sichtbare **Sternchen**, unnötige Abstände und <span style="color:red">HTML-Reste</span>.\n\n- Punkt eins\n- Punkt zwei',
    after: 'Projektstatus\n\nDieser Text enthält fremde Schriftarten, sichtbare Sternchen, unnötige Abstände und HTML-Reste.\n\n• Punkt eins\n• Punkt zwei',
    guideTitle: 'Textformatierung in vier Schritten entfernen',
    guide: [
      'Formatierten Text aus Word, einer Website, E-Mail oder KI-Antwort einfügen.',
      'Den Modus „Sauberer Klartext“ ausgewählt lassen.',
      'In der Vorschau kontrollieren, ob Absätze und Listen sinnvoll erhalten wurden.',
      'Den Klartext kopieren oder als TXT, PDF oder anderes Format herunterladen.',
    ],
    faqs: [
      { question: 'Wie kann ich Formatierung aus einem Text entfernen?', answer: 'Füge den Text ein und wähle „Sauberer Klartext“. Kopivaro entfernt Rich-Text-, HTML- und Markdown-Formatierung und erhält dabei verständliche Absätze und Listen.' },
      { question: 'Werden Schriftarten und Farben entfernt?', answer: 'Ja. Fremde Schriftarten, Textfarben, Hintergrundfarben, Inline-Styles und Word-spezifische Formatierungsreste werden nicht in den Klartext übernommen.' },
      { question: 'Kann ich nur doppelte Leerzeichen entfernen?', answer: 'Ja. Unter „Bereinigung anpassen“ lassen sich Leerzeichen-, Unicode-, Markdown- und weitere Optionen unabhängig voneinander ein- oder ausschalten.' },
      { question: 'Wird der Text zum Entfernen der Formatierung hochgeladen?', answer: 'Nein. Eingabe, Bereinigung, Vorschau und Download laufen ausschließlich im Browser.' },
    ],
  },
  analysis: {
    key: 'analysis',
    eyebrow: 'Lokaler Stil-Check',
    title: 'KI-Text erkennen? Sprachmuster transparent prüfen',
    intro: 'Analysiere Wortzahl, Satzlängen, Lesbarkeit, Wiederholungen und typische KI-Stilmerkmale – ohne Upload und ohne einen unseriösen Herkunfts-Prozentwert.',
    quickAnswer: 'Ein KI-Text lässt sich anhand des fertigen Wortlauts nicht sicher erkennen. Kopivaro prüft deshalb lokal messbare Merkmale wie Satzlängen, Lesbarkeit, Wiederholungen und formelhafte Wendungen und zeigt sie als redaktionelle Hinweise – nicht als Beweis oder KI-Prozentwert.',
    useCases: ['KI-Text-Merkmale transparent prüfen', 'ChatGPT-Texte redaktionell kontrollieren', 'Lesbarkeit und Satzlängen messen', 'Wiederholungen und formelhafte Wendungen finden'],
    mode: 'plain',
    source: 'auto',
    options: preset({ repairPdfBreaks: false }),
    before: 'In der heutigen Zeit ist es wichtig zu beachten, dass klare Kommunikation von entscheidender Bedeutung ist. Darüber hinaus schafft sie Vertrauen. Zudem verbessert sie die Zusammenarbeit. Abschließend lässt sich festhalten, dass gute Texte wichtig sind.',
    after: 'Die Analyse zeigt Wort- und Satzzahlen, Lesbarkeit und gefundene Formulierungs- oder Wiederholungsmuster. Sie behauptet nicht, die Herkunft des Textes beweisen zu können.',
    guideTitle: 'Texte sinnvoll auf KI-Stilmuster prüfen',
    guide: [
      'Text einfügen oder eine lokale Datei öffnen und bereinigen.',
      'Unter dem Ergebnis die kompakte „Textanalyse“ aufklappen.',
      'Messwerte und konkret benannte Stilhinweise statt eines Prozentwerts prüfen.',
      'Die Hinweise als Anlass für eine redaktionelle Prüfung verwenden – nicht als Beweis für die Urheberschaft.',
    ],
    faqs: [
      { question: 'Kann Kopivaro sicher erkennen, ob ein Text von ChatGPT stammt?', answer: 'Nein. Aus dem fertigen Text allein lässt sich die Urheberschaft nicht zuverlässig beweisen. Kopivaro zeigt deshalb nur nachvollziehbare Sprachmuster und keine erfundene KI-Wahrscheinlichkeit.' },
      { question: 'Welche Merkmale werden geprüft?', answer: 'Unter anderem Wort- und Satzlängen, Lesbarkeit, Wortvielfalt, wiederholte Satzanfänge sowie typische Einleitungs-, Übergangs- und Schlussformeln.' },
      { question: 'Wird der analysierte Text hochgeladen?', answer: 'Nein. Auch die Textanalyse läuft ausschließlich im Browser und verwendet keine KI-API.' },
      { question: 'Kann ein menschlicher Text dieselben Merkmale haben?', answer: 'Ja. Formelhafte oder sehr gleichmäßige Texte können vollständig von Menschen stammen. Umgekehrt können KI-Texte unauffällig wirken.' },
    ],
  },
  'pdf-export': {
    key: 'pdf-export',
    eyebrow: 'Text → PDF',
    title: 'Text lokal in eine saubere PDF umwandeln',
    intro: 'Bereinige Text, Markdown oder ein Word-Dokument und lade das Ergebnis anschließend als übersichtliche PDF herunter – direkt im Browser.',
    quickAnswer: 'Kopivaro wandelt Text kostenlos und ohne Upload in eine PDF um. Überschriften, Absätze, Listen, Zitate, Code und einfache Tabellen werden in ein ruhiges A4-Dokument mit Seitenzahlen übertragen; die Datei entsteht lokal im Browser.',
    useCases: ['Text als PDF speichern', 'TXT oder Markdown in PDF umwandeln', 'Word-Inhalt vereinfacht als PDF ausgeben', 'Besprechungsnotizen als A4-PDF herunterladen'],
    mode: 'word',
    source: 'auto',
    options: preset(),
    before: '## Besprechungsnotiz\n\n**Datum:** 22. Juli\n\n- Entwurf prüfen\n- Freigabe dokumentieren',
    after: 'Eine mehrseitige PDF mit Überschrift, Absatz, Liste, sauberem Seitenumbruch und Seitenzahlen.',
    guideTitle: 'In vier Schritten zur PDF',
    guide: [
      'Text einfügen oder PDF, DOCX, HTML, Markdown beziehungsweise TXT lokal öffnen.',
      'Zielformat und Bereinigungsoptionen wählen und die Vorschau prüfen.',
      'Im Download-Menü „PDF“ auswählen.',
      '„Herunterladen“ anklicken – die PDF entsteht vollständig im Browser.',
    ],
    faqs: [
      { question: 'Wird die PDF auf einem Server erzeugt?', answer: 'Nein. Die PDF-Datei wird lokal im Browser erstellt und direkt als Download bereitgestellt.' },
      { question: 'Welche Struktur übernimmt die PDF?', answer: 'Überschriften, Absätze, Listen, Zitate, Codeblöcke und einfache Tabellen werden in ein ruhiges A4-Layout mit Seitenzahlen übertragen.' },
      { question: 'Kann ich zuerst eine DOCX- oder PDF-Datei öffnen?', answer: 'Ja. Unterstützte Dateien werden lokal gelesen. Anschließend kannst du den extrahierten Inhalt bereinigen und wieder als PDF speichern.' },
      { question: 'Bleiben sehr komplexe Layouts exakt erhalten?', answer: 'Nein. Kopivaro erzeugt bewusst ein vereinfachtes Textdokument und ist kein pixelgenauer Office- oder Druckvorstufen-Konverter.' },
    ],
  },
  'chatgpt-word': {
    key: 'chatgpt-word',
    eyebrow: 'ChatGPT → Word',
    title: 'ChatGPT-Text für Word bereinigen und sauber einfügen',
    intro: 'Entferne störende ChatGPT-Formatierung oder wandle Markdown-Überschriften, Sternchen und Listen in echte Word- und Google-Docs-Formatierung um.',
    quickAnswer: 'Kopivaro bereitet ChatGPT-Text für Word und Google Docs vor. Markdown-Sternchen und Rauten werden in sichtbare Formatierung umgewandelt, Listen bleiben Listen und fremde Farben oder Schriftarten werden entfernt. Danach kannst du formatiert kopieren oder eine DOCX-Datei herunterladen.',
    useCases: ['ChatGPT-Text in Word einfügen', 'ChatGPT-Formatierung entfernen', 'KI-Markdown in Word-Format umwandeln', 'ChatGPT-Antwort als DOCX speichern'],
    mode: 'word',
    source: 'markdown',
    options: preset({ repairPdfBreaks: false }),
    before: '## Wochenplan\n\n**Montag:** Recherche\n\n- Quellen prüfen\n- Notizen ordnen',
    after: 'Wochenplan\n\nMontag: Recherche\n\n• Quellen prüfen\n• Notizen ordnen',
    guideTitle: 'ChatGPT-Antwort für ein Dokument vorbereiten',
    guide: [
      'Die vollständige KI-Antwort kopieren und oben einfügen.',
      'Die Erkennung „Markdown“ beibehalten oder manuell wählen.',
      '„Für Word & Docs“ auswählen und die Vorschau kontrollieren.',
      'Formatiert kopieren oder direkt als DOCX herunterladen.',
    ],
    faqs: [
      { question: 'Warum erscheinen in Word manchmal sichtbare Sternchen?', answer: 'Viele KI-Antworten verwenden Markdown. Kopivaro wandelt diese Zeichen vor dem Kopieren in echte Fett- und Kursivformatierung um.' },
      { question: 'Bleiben Listen echte Listen?', answer: 'Ja. Erkannte Markdown- und HTML-Listen werden als semantische Listen aufgebaut und beim formatierten Kopieren beziehungsweise im DOCX-Export erhalten.' },
      { question: 'Werden Inhalte meiner Unterhaltung gespeichert?', answer: 'Nein. Der eingefügte Text verbleibt nur im Arbeitsspeicher der geöffneten Browserseite.' },
    ],
  },
  pdf: {
    key: 'pdf',
    eyebrow: 'PDF → flüssiger Text',
    title: 'PDF-Text bereinigen und Zeilenumbrüche entfernen',
    intro: 'Entferne künstliche Zeilenumbrüche und eindeutige Silbentrennungen aus kopiertem PDF-Text, ohne echte Absätze, Listen und kurze Überschriften zusammenzuziehen.',
    quickAnswer: 'Kopivaro bereinigt kopierten PDF-Text, indem wahrscheinlich künstliche Zeilenumbrüche verbunden und eindeutige Trennungen vorsichtig zusammengeführt werden. Leerzeilen, Listenpunkte und wahrscheinliche Überschriften bleiben Grenzen.',
    useCases: ['PDF-Zeilenumbrüche entfernen', 'Kopierten PDF-Text in Fließtext umwandeln', 'Silbentrennungen aus PDF-Text verbinden', 'Absätze und Aufzählungen beim Bereinigen bewahren'],
    mode: 'plain',
    source: 'pdf',
    options: preset({ convertMarkdown: false }),
    before: 'Bericht zur Planung\n\nDie nächsten Schritte werden im gemein-\nsamen Termin abgestimmt und danach\nschriftlich festgehalten.\n\n• Budget prüfen\n• Termin bestätigen',
    after: 'Bericht zur Planung\n\nDie nächsten Schritte werden im gemeinsamen Termin abgestimmt und danach schriftlich festgehalten.\n\n• Budget prüfen\n• Termin bestätigen',
    guideTitle: 'PDF-Zeilenumbrüche kontrolliert entfernen',
    guide: [
      'Text direkt aus der PDF kopieren und in das Eingabefeld einsetzen.',
      'Als Quelle „PDF-ähnlicher Text“ wählen, falls die Erkennung abweicht.',
      'Die Optionen für Zeilenumbrüche und Silbentrennungen aktiviert lassen.',
      'Fachbegriffe und Eigennamen in der Vorher-Nachher-Ansicht kontrollieren.',
    ],
    faqs: [
      { question: 'Werden echte Absätze erhalten?', answer: 'Ja. Leerzeilen gelten als Absatzgrenzen und werden bei der PDF-Reparatur nicht überschritten.' },
      { question: 'Kann eine Silbentrennung falsch verbunden werden?', answer: 'Bei mehrdeutigen Fachbegriffen ist das möglich. Die Reparatur arbeitet konservativ; prüfe dennoch Eigennamen und ungewöhnliche Zusammensetzungen.' },
      { question: 'Werden Aufzählungen zusammengezogen?', answer: 'Nein. Zeilen, die wie Listenpunkte aussehen, bleiben eigenständig.' },
    ],
  },
  markdown: {
    key: 'markdown',
    eyebrow: 'Sternchen und Rauten loswerden',
    title: 'Markdown entfernen, Inhalt und Struktur behalten',
    intro: 'Wandle sichtbare Markdown-Zeichen in normale Formatierung oder sauberen Klartext um. Überschriften, Links und Listen bleiben verständlich.',
    quickAnswer: 'Kopivaro entfernt Markdown-Zeichen wie Rauten, Sternchen, Unterstriche und Backticks und wandelt den Inhalt in lesbaren Klartext oder sichtbare Formatierung um. Links, Überschriften, Listen und Code bleiben je nach gewähltem Ziel verständlich erhalten.',
    useCases: ['Markdown entfernen', 'Markdown in Klartext umwandeln', 'ChatGPT-Markdown ohne Sternchen kopieren', 'Markdown für Word oder E-Mail formatieren'],
    mode: 'plain',
    source: 'markdown',
    options: preset({ repairPdfBreaks: false }),
    before: '# Ergebnis\n\nDas ist **wichtig** und hier steht [die Quelle](https://example.org).\n\n- Punkt eins\n- Punkt zwei',
    after: 'Ergebnis\n\nDas ist wichtig und hier steht die Quelle (https://example.org).\n\n• Punkt eins\n• Punkt zwei',
    guideTitle: 'Markdown in drei Schritten entfernen',
    guide: [
      'Markdown-Text einfügen und die erkannte Quelle kontrollieren.',
      'Für komplett unformatierten Text „Sauberer Klartext“ auswählen.',
      'Für Word oder E-Mail stattdessen den passenden formatierten Modus wählen.',
    ],
    faqs: [
      { question: 'Was passiert mit Links?', answer: 'Im Klartext bleibt die Zieladresse lesbar. In Word, E-Mail und HTML bleibt der Link klickbar.' },
      { question: 'Werden Codeblöcke erhalten?', answer: 'Ja. Im Klartext bleibt ihr Inhalt stehen; in Markdown und formatierten Modi werden sie als eigener Block ausgegeben.' },
      { question: 'Kann ich Markdown auch erzeugen statt entfernen?', answer: 'Ja. Wähle den Ausgabemodus „Markdown“, um Rich Text oder HTML in gut lesbares Markdown umzuwandeln.' },
    ],
  },
  linebreaks: {
    key: 'linebreaks',
    eyebrow: 'Absätze statt Zeilensalat',
    title: 'Unnötige Zeilenumbrüche entfernen, Absätze bewahren',
    intro: 'Fasse wahrscheinlich künstlich umbrochene Textzeilen zusammen. Leerzeilen, Überschriften und Listen werden als Grenzen behandelt.',
    quickAnswer: 'Kopivaro entfernt unnötige harte Zeilenumbrüche, verbindet aber nicht blind jede Zeile. Echte Absätze mit Leerzeilen, Aufzählungen und wahrscheinliche Überschriften bleiben getrennt.',
    useCases: ['Zeilenumbrüche online entfernen', 'Harte Umbrüche in Fließtext umwandeln', 'Absätze beim Entfernen von Umbrüchen behalten', 'Kopierten PDF- oder E-Mail-Text zusammenführen'],
    mode: 'plain',
    source: 'pdf',
    options: preset({ joinHyphenation: false, convertMarkdown: false }),
    before: 'Hinweis\n\nDieser Satz wurde beim Kopieren\nin mehrere kurze Zeilen zerlegt,\ngehört aber in einen Absatz.\n\nDer nächste Absatz bleibt getrennt.',
    after: 'Hinweis\n\nDieser Satz wurde beim Kopieren in mehrere kurze Zeilen zerlegt, gehört aber in einen Absatz.\n\nDer nächste Absatz bleibt getrennt.',
    guideTitle: 'Harte Umbrüche sinnvoll reduzieren',
    guide: [
      'Text mit den störenden Umbrüchen einfügen.',
      '„PDF-ähnlicher Text“ als Quelle auswählen.',
      'Die Vorschau auf Absatzgrenzen und Überschriften prüfen.',
      'Als Klartext kopieren oder als TXT speichern.',
    ],
    faqs: [
      { question: 'Was ist ein harter Zeilenumbruch?', answer: 'Ein harter Umbruch beendet eine Zeile technisch, obwohl der Satz inhaltlich weitergeht. Das tritt häufig beim Kopieren aus PDFs auf.' },
      { question: 'Werden alle Umbrüche entfernt?', answer: 'Nein. Kopivaro verbindet nur Zeilen mit deutlichen Merkmalen eines künstlichen Umbruchs.' },
      { question: 'Kann ich Silbentrennungen getrennt steuern?', answer: 'Ja. Die Option zum Zusammenführen von Trennungen ist unabhängig von der allgemeinen Zeilenreparatur schaltbar.' },
    ],
  },
  whatsapp: {
    key: 'whatsapp',
    eyebrow: 'Lesbar im Messenger',
    title: 'Texte für WhatsApp passend formatieren',
    intro: 'Übersetze Überschriften, Hervorhebungen und Listen in eine kompakte, WhatsApp-taugliche Schreibweise – ohne HTML-Reste.',
    quickAnswer: 'Kopivaro formatiert Text für WhatsApp, indem Fettung in Sternchen, Kursivschrift in Unterstriche und Durchstreichungen in Tilden umgewandelt werden. Listen und Links bleiben als gut lesbarer Nachrichtentext erhalten.',
    useCases: ['WhatsApp-Text formatieren', 'Fett, kursiv und durchgestrichen für WhatsApp erzeugen', 'Word- oder ChatGPT-Text in WhatsApp einfügen', 'Lange Nachrichten übersichtlich strukturieren'],
    mode: 'whatsapp',
    source: 'markdown',
    options: preset({ repairPdfBreaks: false }),
    before: '## Termin\n\n**Wann:** Dienstag, 14 Uhr\n\n- Unterlagen mitbringen\n- Kurz bestätigen',
    after: '*Termin*\n\n*Wann:* Dienstag, 14 Uhr\n\n• Unterlagen mitbringen\n• Kurz bestätigen',
    guideTitle: 'Nachricht für WhatsApp vorbereiten',
    guide: [
      'Text aus Word, einer KI-Antwort oder E-Mail einfügen.',
      'Den Modus „Für WhatsApp“ ausgewählt lassen.',
      'Vorschau auf Länge und Zeilenabstände prüfen.',
      'Ausgabe kopieren und in den gewünschten Chat einsetzen.',
    ],
    faqs: [
      { question: 'Welche WhatsApp-Formatierung wird verwendet?', answer: 'Fett wird mit Sternchen, kursiv mit Unterstrichen und durchgestrichen mit Tilden dargestellt.' },
      { question: 'Bleiben Links klickbar?', answer: 'Kopivaro schreibt die vollständige URL in die Ausgabe. WhatsApp erkennt unterstützte Webadressen anschließend selbst.' },
      { question: 'Was passiert mit Tabellen?', answer: 'Tabellen werden als kompakte Textzeilen mit Trennzeichen ausgegeben. Prüfe bei breiten Tabellen die Lesbarkeit auf dem Smartphone.' },
    ],
  },
  html: {
    key: 'html',
    eyebrow: 'Weniger Code, weniger Risiko',
    title: 'HTML bereinigen und auf semantische Elemente reduzieren',
    intro: 'Entferne Skripte, Event-Handler, Word-Klassen, Inline-Design und aktive Einbettungen. Übrig bleibt übersichtliches HTML für redaktionelle Inhalte.',
    quickAnswer: 'Kopivaro bereinigt HTML lokal und entfernt Skripte, Event-Handler, gefährliche Links, aktive Einbettungen, Word-Klassen und Inline-Styles. Erlaubte Überschriften, Absätze, Listen, Links, Zitate, Code und einfache Tabellen bleiben als semantisches HTML erhalten.',
    useCases: ['HTML-Code bereinigen', 'Word-HTML und Inline-Styles entfernen', 'Skripte und Event-Handler aus HTML löschen', 'HTML-Tags in semantische Grundstruktur vereinfachen'],
    mode: 'html',
    source: 'html',
    options: preset({ repairPdfBreaks: false }),
    before: '<p class="MsoNormal" style="color:red" onclick="alert(1)"><span><b>Hinweis</b></span></p><script>alert(1)</script>',
    after: '<p><strong>Hinweis</strong></p>',
    guideTitle: 'HTML sicher vereinfachen',
    guide: [
      'HTML oder Rich Text in das Eingabefeld einfügen.',
      'Als Quelle „HTML / Rich Text“ oder „Word / Office“ wählen.',
      'Die gerenderte Vorschau und die Änderungsübersicht prüfen.',
      'Als HTML herunterladen oder über „Klartext kopieren“ den Quelltext übernehmen.',
    ],
    faqs: [
      { question: 'Welche Elemente werden blockiert?', answer: 'Unter anderem Skripte, Formulare, Frames, Objekte, Medien, Bilder, Event-Handler und gefährliche Linkprotokolle.' },
      { question: 'Warum werden Styles entfernt?', answer: 'Fremde Schriftarten, Farben und Layoutregeln führen beim Einfügen oft zu Konflikten. Kopivaro konzentriert sich deshalb auf die Inhaltsstruktur.' },
      { question: 'Ist jedes bereinigte HTML automatisch für jede Website geeignet?', answer: 'Nein. Prüfe den Code zusätzlich im Kontext deines Systems und beachte dessen eigene Sicherheits- und Inhaltsregeln.' },
    ],
  },
};

export const modeLabels: Record<OutputMode, { title: string; short: string; description: string }> = {
  word: { title: 'Für Word & Docs', short: 'Word', description: 'Überschriften, Listen, Links und Hervorhebungen bleiben erhalten.' },
  email: { title: 'Für E-Mail', short: 'E-Mail', description: 'Kompatibles, reduziertes HTML für neue Nachrichten.' },
  whatsapp: { title: 'Für WhatsApp', short: 'WhatsApp', description: 'Messenger-Syntax für Fett, Kursiv, Listen und Links.' },
  plain: { title: 'Sauberer Klartext', short: 'Klartext', description: 'Formatierung entfernen, Absätze und lesbare Listen bewahren.' },
  html: { title: 'Sauberes HTML', short: 'HTML', description: 'Semantisches HTML ohne Skripte, Styles oder Altlasten.' },
  markdown: { title: 'Markdown', short: 'Markdown', description: 'Struktur in gut lesbares Markdown umwandeln.' },
};

export const optionLabels: Array<{ key: keyof CleaningOptions; label: string; help: string }> = [
  { key: 'duplicateSpaces', label: 'Doppelte Leerzeichen entfernen', help: 'Mehrere Leerzeichen innerhalb einer Zeile reduzieren.' },
  { key: 'excessBlankLines', label: 'Zu viele Leerzeilen reduzieren', help: 'Höchstens zwei sichtbare Leerzeilen zwischen Inhalten belassen.' },
  { key: 'normalizeNbsp', label: 'Geschützte Leerzeichen normalisieren', help: 'Nicht umbrechende Leerzeichen durch normale ersetzen.' },
  { key: 'removeZeroWidth', label: 'Unsichtbare Zeichen entfernen', help: 'Zero-Width- und Byte-Order-Zeichen löschen.' },
  { key: 'removeSoftHyphens', label: 'Weiche Trennzeichen entfernen', help: 'Unsichtbare Soft Hyphens entfernen.' },
  { key: 'normalizeSpecialSpaces', label: 'Sonderleerzeichen normalisieren', help: 'Typografische Leerzeichen vereinheitlichen.' },
  { key: 'convertMarkdown', label: 'Markdown erkennen und umwandeln', help: 'Sternchen, Rauten, Listen und Links interpretieren.' },
  { key: 'removeUnnecessaryHtml', label: 'Unnötiges HTML entfernen', help: 'Klassen, Styles, Spans und aktive Elemente entfernen.' },
  { key: 'repairPdfBreaks', label: 'PDF-Zeilenumbrüche reparieren', help: 'Wahrscheinlich künstliche harte Umbrüche konservativ verbinden.' },
  { key: 'joinHyphenation', label: 'Silbentrennungen zusammenführen', help: 'Eindeutige Trennungen aus Buchstaben, Bindestrich und Umbruch verbinden.' },
  { key: 'detectLists', label: 'Listen erkennen', help: 'Zeilen mit Aufzählungszeichen oder Nummern in echte Listen umwandeln.' },
  { key: 'preserveParagraphs', label: 'Absatzstruktur erhalten', help: 'Leerzeilen weiterhin als Absatzgrenzen behandeln.' },
  { key: 'fixPunctuationSpacing', label: 'Satzzeichenabstände korrigieren', help: 'Leerzeichen vor und fehlende Abstände nach Satzzeichen bereinigen.' },
];
